import { Group, Loader } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { CountryIso3166 } from "@tfg/db/browser"
import {
  CountryCell,
  DateCell,
  getCheckboxColumnDefs,
  Missing,
  TextCell,
  Tooltip,
} from "@tfg/ui"
import { Dispatch, SetStateAction, useMemo } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import {
  businessRelationshipDtoTypeEnum,
  CreateBusinessPartnerRequestDto,
  FindContactResponseDto,
  SupplierDto,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import { RiskScoreDisplay } from "~/pages/Brand/Suppliers/List/components/RiskScore"
import { RequestsRatioCell } from "~/pages/Brand/Suppliers/SuppliersRepository/components/RequestsRatioCell"
import { SupplierActionsMenu } from "~/pages/Brand/Suppliers/SuppliersRepository/components/SupplierActionsMenu"
import { COUNTRIES } from "~/utils/countries"

export type SupplierFormValues =
  | CreateBusinessPartnerRequestDto
  | (UpdateBusinessPartnerRequestDto & { contactId: string })

function constructSupplierAddress(
  street: string | null,
  city: string | null,
  postalCode: string | null,
  country?: CountryIso3166 | null
) {
  if (
    !street ||
    !city ||
    street === "not-entered-yet" ||
    city === "not-entered-yet"
  ) {
    return <Missing />
  }
  const addressParts = [street, city]
  if (postalCode) {
    addressParts.push(postalCode)
  }
  if (country) {
    addressParts.push(COUNTRIES[country].countryName)
  }
  const address = addressParts.join(", ")
  return address.endsWith(",") ? address.slice(0, -1) : address
}

export const useColumns = ({
  setInitialValues,
  setFormType,
}: {
  setInitialValues: Dispatch<SetStateAction<SupplierFormValues>>
  openDrawer: () => void
  setFormType: Dispatch<SetStateAction<"create" | "update">>
}) => {
  const { t } = useTranslation(["traceability", "suppliers"])
  const columnHelper = createColumnHelper<FindContactResponseDto>()

  return useMemo<ColumnDef<FindContactResponseDto, any>[]>(() => {
    const update = async (values: FindContactResponseDto) => {
      setInitialValues({
        contactId: values.id,
        supplierId: values.supplier.id,
        name: values.supplier.name,
        email: values.supplier.email,
        preferedLangageIso6391: values.supplier.preferedLangageIso6391,
        organization: {
          businessRelationship: {
            isActive:
              values.supplier.organization.businessRelationship.isActive,
            supplierCode:
              values.supplier.organization.businessRelationship.supplierCode ??
              null,
            type:
              values.supplier.organization.businessRelationship.type ?? null,
            tagNames:
              values.supplier.organization.businessRelationship.tagNames ?? [],
          },
          name: values.supplier.organization.name,
          street:
            values.supplier.organization.street !== "not-entered-yet"
              ? (values.supplier.organization.street ?? "")
              : "",
          city:
            values.supplier.organization.city !== "not-entered-yet"
              ? (values.supplier.organization.city ?? "")
              : "",
          postalCode: values.supplier.organization.postalCode ?? "",
          country: values.supplier.organization.country ?? CountryIso3166.FR,
          registrationId: values.supplier.organization.registrationIds ?? [],
          coordinates: values.supplier.organization.coordinates ?? null,
        },
      })
      setFormType("update")
    }

    return [
      getCheckboxColumnDefs<FindContactResponseDto>(),
      columnHelper.accessor(
        "supplier.organization.businessRelationship.isActive",
        {
          id: "status",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.status.name")}
              tooltip={`${t("contactTable.columns.status.name")} : ${t("contactTable.columns.status.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => (
            <TextCell
              value={
                getValue()
                  ? t("contactTable.status.active")
                  : t("contactTable.status.inactive")
              }
            />
          ),
        }
      ),
      columnHelper.accessor("supplier.organization.businessRelationship.type", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("contactTable.columns.type.name")}
            tooltip={`${t("contactTable.columns.type.name")} : ${t("contactTable.columns.type.tooltip")}`}
          />
        ),
        enableSorting: false,
        cell: ({ getValue }) => {
          const { success, data: value } = z
            .enum(businessRelationshipDtoTypeEnum)
            .safeParse(getValue())

          if (!success) {
            return <Missing />
          }

          return (
            <TextCell
              value={t(`contactModal.labels.companyRelationshipType.${value}`)}
            />
          )
        },
      }),
      columnHelper.accessor("supplier.organization.name", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("contactTable.columns.company.name")}
            tooltip={`${t("contactTable.columns.company.name")} : ${t("contactTable.columns.company.tooltip")}`}
          />
        ),
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor(
        (originalRow) =>
          originalRow.supplier.organization.businessRelationship.supplierCode ??
          null,
        {
          id: "supplierCode",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.code.name")}
              tooltip={`${t("contactTable.columns.code.name")} : ${t("contactTable.columns.code.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => {
            const value = getValue()
            return value ? <TextCell value={value} /> : <Missing />
          },
        }
      ),
      columnHelper.accessor(
        (originalRow) =>
          originalRow.supplier.organization.businessRelationship.tagNames ?? [],
        {
          id: "tags",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.tags.name")}
              tooltip={`${t("contactTable.columns.tags.name")} : ${t("contactTable.columns.tags.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => {
            const tags = getValue()
            return tags.length > 0 ? (
              <TextCell value={tags.join(", ")} />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor(
        (originalRow) => ({
          street: originalRow.supplier.organization.street,
          city: originalRow.supplier.organization.city,
          postalCode: originalRow.supplier.organization.postalCode,
          country: originalRow.supplier.organization.country,
        }),
        {
          id: "address",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.location.name")}
              tooltip={`${t("contactTable.columns.location.name")} : ${t("contactTable.columns.location.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => {
            const { street, city, postalCode, country } = getValue()
            const addressTooltip = constructSupplierAddress(
              street,
              city,
              postalCode,
              country
            )

            return (
              <CountryCell
                tooltip={
                  typeof addressTooltip === "string"
                    ? addressTooltip
                    : undefined
                }
                countriesMapper={COUNTRIES}
                countryIso3166={country}
              />
            )
          },
        }
      ),
      columnHelper.accessor(
        (originalRow) => originalRow.supplier.organization.productionSites,
        {
          id: "productionSites",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.productionSites.name")}
              tooltip={`${t("contactTable.columns.productionSites.name")} : ${t("contactTable.columns.productionSites.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => {
            const productionSites = getValue()
            const count = productionSites.length
            const tooltipContent =
              productionSites.length > 0
                ? productionSites.map(
                    (
                      productionSite: FindContactResponseDto["supplier"]["organization"]["productionSites"][number]
                    ) => {
                      const countryName = productionSite.countryIso3166
                        ? (COUNTRIES[productionSite.countryIso3166]
                            ?.countryName ?? productionSite.countryIso3166)
                        : null
                      return `${productionSite.name}${countryName ? ` - ${countryName}` : ""}`
                    }
                  )
                : []

            return (
              <Tooltip
                label={
                  <Group>
                    {tooltipContent.map((productionSite: string) => (
                      <span key={productionSite}>{productionSite}</span>
                    ))}
                  </Group>
                }
              >
                <TextCell value={count} />
              </Tooltip>
            )
          },
        }
      ),
      columnHelper.accessor("supplier.name", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("contactTable.columns.contactName.name")}
            tooltip={`${t("contactTable.columns.contactName.name")} : ${t("contactTable.columns.contactName.tooltip")}`}
          />
        ),
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("supplier.email", {
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("contactTable.columns.contactEmail.name")}
            tooltip={`${t("contactTable.columns.contactEmail.name")} : ${t("contactTable.columns.contactEmail.tooltip")}`}
          />
        ),
        id: "email",
        enableSorting: false,
        cell: ({ getValue }) => (
          <TextCell value={getValue()} copyable={true} onCopy={() => {}} />
        ),
      }),
      columnHelper.accessor(
        (originalRow) =>
          (
            originalRow.supplier as SupplierDto & {
              lastLogin?: Date | null
              lastEmailSent?: Date | null
            }
          ).lastLogin ?? null,
        {
          id: "lastLogin",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.activity.name")}
              tooltip={`${t("contactTable.columns.activity.name")} : ${t("contactTable.columns.activity.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => (
            <DateCell date={getValue()} format="l HH:mm" />
          ),
          enablePinning: true,
        }
      ),
      columnHelper.accessor(
        (originalRow) =>
          (
            originalRow.supplier as SupplierDto & {
              lastLogin?: Date | null
              lastEmailSent?: Date | null
            }
          ).lastEmailSent ?? null,
        {
          id: "lastEmailSent",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.reminder.name")}
              tooltip={`${t("contactTable.columns.reminder.name")} : ${t("contactTable.columns.reminder.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => (
            <DateCell date={getValue()} format="l HH:mm" />
          ),
          enablePinning: true,
        }
      ),
      columnHelper.accessor(
        (originalRow) => originalRow.supplier.averageResponseTime ?? 0,
        {
          id: "averageResponseTime",
          header: () => (
            <TextCell
              fw="var(--table-header-text-weight)"
              value={t("contactTable.columns.averageResponseTime.name")}
              tooltip={`${t("contactTable.columns.averageResponseTime.name")} : ${t("contactTable.columns.averageResponseTime.tooltip")}`}
            />
          ),
          enableSorting: false,
          cell: ({ getValue }) => {
            const value = getValue()
            return value !== null ? (
              <TextCell
                value={t(
                  "contactTable.columns.averageResponseTime.averageResponseTimeValue",
                  {
                    nbDays: value,
                    count: value,
                  }
                )}
              />
            ) : (
              <Missing />
            )
          },
        }
      ),
      columnHelper.accessor("supplier.risk.collaborationRisk", {
        id: "collaborationRisk",
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("suppliers:risk.table.collaborationRisk.title")}
            tooltip={t("suppliers:risk.table.collaborationRisk.tooltip")}
          />
        ),
        enableSorting: false,
        cell: (info) => {
          const risk = info.getValue()
          return risk !== undefined ? (
            <Group gap="xs">
              <RiskScoreDisplay risk={risk} />
            </Group>
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("supplier.risk.certificateRisk", {
        id: "certificateRisk",
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("suppliers:risk.table.certificationRisk.title")}
            tooltip={t("suppliers:risk.table.certificationRisk.tooltip")}
          />
        ),
        enableSorting: false,
        cell: (info) => {
          const risk = info.getValue()
          return risk !== undefined ? (
            <Group>
              <RiskScoreDisplay risk={risk} />
            </Group>
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor((originalRow) => originalRow.supplier, {
        id: "requests",
        header: () => (
          <TextCell
            fw="var(--table-header-text-weight)"
            value={t("contactTable.columns.requests.name")}
            tooltip={`${t("contactTable.columns.requests.name")} : ${t("contactTable.columns.requests.tooltip")}`}
          />
        ),
        enableSorting: false,
        size: 270,
        maxSize: 300,
        cell: ({ getValue }) => {
          const supplier = getValue()
          return <RequestsRatioCell supplier={supplier} />
        },
      }),
      columnHelper.display({
        header: "Actions",
        id: "action-cell",
        enableSorting: false,
        cell: ({ row: { original } }) => {
          return original?.supplier ? (
            <Group>
              <SupplierActionsMenu
                id={original.id}
                onUpdate={() => update(original)}
                contact={original.supplier}
                canDelete={
                  original.supplier.organization.businessRelationship.type !==
                  businessRelationshipDtoTypeEnum.BRAND
                }
              />
            </Group>
          ) : (
            <Loader />
          )
        },
      }),
    ]
  }, [columnHelper, setFormType, setInitialValues, t])
}
